import { useCallback, useEffect, useMemo } from "react";

import { UsePaginationFetchMoreProps } from "components/Table/Pagination/types";
import useFiltersVariables from "components/Filters/useFiltersVariables";

import { IacManagementGroupBy } from "../types";
import useIacManagementStackGroups from "./useIacManagementStackGroups";
import useIacManagementProvidersGroups from "./useIacManagementProvidersGroups";
import useIacManagementTypesGroups from "./useIacManagementTypesGroups";
import { getStackPredicate } from "../helpers";
import { makeGroupSortOption } from "./helpers";
import { FILTERS_TYPE, initialSortDirection, initialSortOption } from "../ListLayout/constants";
import useIacTableStore from "../components/Table/useIacTableStore";

type UseIacManagementGroupsProps = {
  groupBy: IacManagementGroupBy;
  parentStackId?: string;
};

const useIacManagementGroups = ({ groupBy, parentStackId }: UseIacManagementGroupsProps) => {
  // TODO: merge these hooks to a generic one
  const stackGroupsHook = useIacManagementStackGroups(parentStackId);
  const providerGroupsHook = useIacManagementProvidersGroups(parentStackId);
  const providerTypesHook = useIacManagementTypesGroups(parentStackId);

  const { filterVariables, sortingMounted } = useFiltersVariables({
    filterType: FILTERS_TYPE,
    initialSortDirection,
    initialSortOption,
  });

  const currentHook = useMemo(() => {
    switch (groupBy) {
      case IacManagementGroupBy.Provider:
        return providerGroupsHook;
      case IacManagementGroupBy.Type:
        return providerTypesHook;
      case IacManagementGroupBy.Stack:
      case IacManagementGroupBy.None:
      default:
        return stackGroupsHook;
    }
  }, [groupBy, providerGroupsHook, stackGroupsHook, providerTypesHook]);

  const { lazyFetch } = currentHook;

  const isEmpty = !!(
    currentHook.dataIsEmpty &&
    !filterVariables.searchInput &&
    !filterVariables.predicates.length
  );
  const searchIsEmpty =
    currentHook.dataIsEmpty &&
    !!(filterVariables.searchInput || filterVariables.predicates.length > 0);

  const fetchData = useCallback(
    ({ cursor, requestedPage, rowsPerPage }: UsePaginationFetchMoreProps) => {
      const predicates =
        (parentStackId && [...filterVariables.predicates, getStackPredicate(parentStackId)]) ||
        filterVariables.predicates;

      if (sortingMounted) {
        lazyFetch({
          variables: {
            ...(parentStackId && { stackId: parentStackId }),
            input: {
              after: cursor,
              first: rowsPerPage,
              fullTextSearch: filterVariables.searchInput,
              orderBy: makeGroupSortOption({
                groupBy,
                actualSortOption: filterVariables.sortOptionFields,
              }),
              predicates,
              requestedPage: requestedPage,
            },
          },
        });
      }
    },
    [
      filterVariables.predicates,
      filterVariables.searchInput,
      filterVariables.sortOptionFields,
      groupBy,
      lazyFetch,
      parentStackId,
      sortingMounted,
    ]
  );

  const loading = currentHook.loading && !currentHook.groups.length;
  const setGroupsLoading = useIacTableStore((state) => state.setGroupsLoading);

  useEffect(() => {
    setGroupsLoading(loading);
  }, [loading, setGroupsLoading]);

  return {
    fetchData,
    isEmpty,
    searchIsEmpty,
    groups: currentHook.groups,
    loading,
    endCursor: currentHook.endCursor,
    startCursor: currentHook.startCursor,
  };
};

export default useIacManagementGroups;
